'use client';

import React, { type AriaRole } from 'react';

import { LazyLoadImage } from '@depop/web-ui-kit/LazyLoadImage';
import clsx from 'clsx';

import styles from './styles.module.css';

import { ExtendedLink } from '@/components/ExtendedLink';
import { useProductCardAspectRatio } from '@/modules/experiments/hooks/useProductCardAspectRatio';
import { buildProductImageProps } from '@/modules/pictures/helpers';
import { type ListProduct } from '@/modules/product/types';

export enum AspectRatio {
  Square = 'square',
  Portrait = 'portrait',
}

export type Props = Pick<ListProduct, 'sellerUserName' | 'preview'> & {
  productHref: string;
  ProductAttributes?: React.FC;
  BoostedTag?: React.FC;
  HoverOverlay?: React.FC;
  Overlay?: React.FC;
  handleClick?: () => void;
  Wrapper?: React.FC<{ children: React.ReactNode }>;
  disableLazyLoading?: boolean;
  role?: AriaRole;
  borderRadius?: 'medium' | 'large';
  aspectRatio?: AspectRatio;
  alt?: string;
};

export function ProductCard({
  productHref,
  preview,
  sellerUserName,
  ProductAttributes,
  HoverOverlay,
  Overlay,
  handleClick,
  Wrapper,
  disableLazyLoading = false,
  role,
  borderRadius = 'medium',
  aspectRatio: aspectRatioProp,
  alt,
}: Props) {
  const { aspectRatio: experimentAspectRatio } = useProductCardAspectRatio();
  const aspectRatio = aspectRatioProp ?? experimentAspectRatio;

  const MaybeLink = Wrapper ? Wrapper : ExtendedLink;
  // Once we're consuming entirely from presentation-api, we can unify the alt tag generation.
  // But this component is used in many places so we need to keep it for now.
  const imageProps = buildProductImageProps({ preview, sellerUserName });
  const imageAlt = alt ?? imageProps.alt;
  const productImageContainerStyles = clsx(
    styles.productImageContainer,
    {
      [styles.borderRadiusMd]: borderRadius === 'medium',
      [styles.borderRadiusLg]: borderRadius === 'large',
    },
    {
      [styles.productImageContainer__withHoverOverlay]: HoverOverlay,
      [styles.productImageContainer__withoutHoverOverlay]: !HoverOverlay,
    },
    {
      [styles.square]: aspectRatio === AspectRatio.Square,
      [styles.portrait]: aspectRatio === AspectRatio.Portrait,
    }
  );

  return (
    <div className={styles.productCardRoot} role={role}>
      <div className={productImageContainerStyles}>
        <MaybeLink
          href={`/products/${productHref}`}
          onClick={handleClick}
          prefetch={false}
          aria-label={imageAlt}
        >
          {Object.keys(preview).length > 0 ? (
            <LazyLoadImage
              disableLazyLoading={disableLazyLoading}
              {...imageProps}
              alt={imageAlt}
            />
          ) : (
            <div className={styles.placeholder} />
          )}
          {Overlay && (
            <div className={styles.overlay}>
              <Overlay />
            </div>
          )}
          {HoverOverlay && (
            <div className={styles.hoverOverlay}>
              <HoverOverlay />
            </div>
          )}
        </MaybeLink>
      </div>
      {ProductAttributes && <ProductAttributes />}
    </div>
  );
}
